import { useState, useEffect, useCallback } from 'react';

const TOUR_PREFIX = 'visionboard_tour_';

export type TourName = 'dashboard' | 'goals' | 'boards';

interface TourProgress {
    completed: boolean;
    skipped: boolean;
    completedAt?: string;
}

export function useTourProgress(tourName: TourName) {
    const storageKey = `${TOUR_PREFIX}${tourName}`;

    const [progress, setProgress] = useState<TourProgress>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch {
            // Ignore parsing errors
        }
        return { completed: false, skipped: false };
    });

    const [shouldShowTour, setShouldShowTour] = useState(false);

    useEffect(() => {
        // Only show tour if not completed and not skipped
        setShouldShowTour(!progress.completed && !progress.skipped);
    }, [progress]);

    const completeTour = useCallback(() => {
        const newProgress: TourProgress = {
            completed: true,
            skipped: false,
            completedAt: new Date().toISOString(),
        };
        localStorage.setItem(storageKey, JSON.stringify(newProgress));
        setProgress(newProgress);
    }, [storageKey]);

    const skipTour = useCallback(() => {
        const newProgress: TourProgress = {
            completed: false,
            skipped: true,
            completedAt: new Date().toISOString(),
        };
        localStorage.setItem(storageKey, JSON.stringify(newProgress));
        setProgress(newProgress);
    }, [storageKey]);

    const resetTour = useCallback(() => {
        localStorage.removeItem(storageKey);
        setProgress({ completed: false, skipped: false });
    }, [storageKey]);

    return {
        shouldShowTour,
        progress,
        completeTour,
        skipTour,
        resetTour,
    };
}

// Function to reset all tours - useful for Settings page
export function resetAllTours() {
    const tourNames: TourName[] = ['dashboard', 'goals', 'boards'];
    tourNames.forEach(name => {
        localStorage.removeItem(`${TOUR_PREFIX}${name}`);
    });
    // Redirect to dashboard to restart
    window.location.href = '/dashboard';
}

// Check if any tour is available
export function hasCompletedAnyTour(): boolean {
    const tourNames: TourName[] = ['dashboard', 'goals', 'boards'];
    return tourNames.some(name => {
        try {
            const stored = localStorage.getItem(`${TOUR_PREFIX}${name}`);
            if (stored) {
                const progress: TourProgress = JSON.parse(stored);
                return progress.completed || progress.skipped;
            }
        } catch {
            // Ignore
        }
        return false;
    });
}
